import {
  matterTypeProviderId,
  roleProviderId,
} from "@smart/bridge-smokeball-basic";
import { FieldNS } from "@smart/bridge-types-basic";
import {
  isNotNullOrUndefined,
  isNullOrUndefined,
  toSentenceCase,
} from "@smart/itops-utils-basic";

import { PickerSelection, PickerSelectionComponent } from "./types";
import {
  GqlMatterLayout,
  GqlMatterType,
  MatterField,
  NewFieldOptions,
} from "../../types";
import {
  ContactType,
  getLabelText,
  mapFieldOptions,
  mapFieldType,
} from "../../utils";

export const hideLayout = (
  matterTypes: GqlMatterType[],
  layout: GqlMatterLayout,
) => layout.providerId === matterTypeProviderId && matterTypes.length < 2;

// Fields on these CUI layouts can't be updated via a layoutItem update, so they're hidden from the map field modal.
const notUpdatableFieldPathsByLayoutId: Record<string, string[]> = {
  PersonalInjurySettlementDetailsItem: [
    "Providers[]/Provider/DisplayName",
    "Providers[]/ServiceStartDate",
    "Providers[]/ServiceEndDate",
    "Providers[]/InvoiceInitialAmount",
    "Providers[]/InvoiceBalance",
    "Providers[]/OverrideCalculations",
    "Providers[]/Invoices[]/*",
    "Providers[]/InvoiceAdjustmentsTotal",
    "Providers[]/LienAsserted",
    "Providers[]/InvoiceTotalClaim",
    "Providers[]/SummaryTotalClaim",
    "Providers[]/LienAmount",
    "Providers[]/FinalAmount",
    "InvoiceInitialTotal",
    "InvoiceBalanceTotal",
    "LiensAndBalancesTotalClaimSum",
    "LiensAndBalancesLienAmountSum",
    "LiensAndBalancesFinalAmountSum",
    "OtherLiensAndBalancesSummaryTotalClaimSum",
    "OtherLiensAndBalancesFinalAmountSum",
    "SettlementAmount",
  ],
};

const isNotUpdatableField = (layoutId: string, fieldName: string) =>
  (notUpdatableFieldPathsByLayoutId[layoutId] || []).some((path) =>
    path.endsWith("/*")
      ? fieldName.startsWith(path.slice(0, -1))
      : fieldName === path,
  );

export const hideField = (
  layout: GqlMatterLayout | undefined,
  field: MatterField,
) => {
  if (layout?.providerId === matterTypeProviderId) {
    if (field.possibleValues?.length === 1) return true;
  }

  if (
    layout?.id &&
    isNotUpdatableField(layout.id, field.originalName || field.name)
  )
    return true;

  return false;
};

export const mapMatterField =
  (matterLayout: GqlMatterLayout) =>
  (matterField: MatterField): NewFieldOptions | undefined => {
    const layout = {
      id: matterLayout.id,
      name: matterLayout.name,
      providerId: matterLayout.providerId,
      parentId: matterLayout.parentId || undefined,
      parentName: matterLayout.parentName || undefined,
      parentProviderId: matterLayout.parentProviderId || undefined,
    };
    const field = {
      name: matterField.name,
      possibleValues: matterField.possibleValues || [],
      dataSourceMapping: matterField.dataSourceMapping || undefined,
      type: matterField.type,
      originalName: matterField.originalName || undefined,
    };
    const uri = FieldNS.generateUri();
    const label = getLabelText(matterField);
    const type = mapFieldType(matterField);

    return (
      type && {
        uri,
        type,
        label,
        layout,
        field,
        supportsCustomValues: isNullOrUndefined(
          matterField.supportsCustomValues,
        )
          ? true
          : matterField.supportsCustomValues,
        ...mapFieldOptions(matterField),
      }
    );
  };

export const isRoleProviderField = (field: MatterField) =>
  ["person", "company", "trust"].some((type) => field.name.startsWith(type));

export const isContactTypeFolder = (folderName: string) =>
  ["person", "company", "trust"].some((type) => folderName.startsWith(type));

export const getGroupUpdateOptions = ({
  selection,
  currentLayoutComponent,
  groupUri,
}: {
  selection: PickerSelection;
  currentLayoutComponent: PickerSelectionComponent;
  groupUri: string | undefined;
}) => {
  if (
    selection.layout &&
    currentLayoutComponent?.type === "role" &&
    selection.fields.filter(isRoleProviderField).length > 0
  )
    return {
      uri: groupUri,
      type: "layoutContact" as const,
      label: toSentenceCase(currentLayoutComponent.displayName),
      layout: {
        id: selection.layout.id,
        name: selection.layout.name,
        providerId: selection.layout.providerId,
        parentId: selection.layout.parentId || undefined,
        parentName: selection.layout.parentName || undefined,
        parentProviderId: selection.layout.parentProviderId || undefined,
      },
      field: {
        name: currentLayoutComponent.name,
        originalName: currentLayoutComponent.originalName,
        possibleValues: [],
        type: "Role",
      },
    };

  return undefined;
};

export const buildFixedSelectionForGroup = ({
  layout,
  groupField,
  fallbackField,
}: {
  layout?: GqlMatterLayout | null;
  groupField?: {
    name: string;
    displayName?: string | null;
    details?: string | null;
    originalName?: string | null;
  } | null;
  fallbackField?: {
    name?: string | null;
    details?: string | null;
  } | null;
}): PickerSelection | undefined => {
  if (!layout) return undefined;

  const groupPrefix = groupField?.details?.split("/").filter(Boolean) || [];
  const fallbackPrefix =
    fallbackField?.details?.split("/").filter(Boolean) || [];
  const prefix = groupPrefix.length ? groupPrefix : fallbackPrefix;

  const groupPathComponents =
    layout.providerId === roleProviderId
      ? groupPrefix.map((path) => ({
          type: "path" as const,
          name: path,
          displayName: path,
        }))
      : [];

  const presetComponents = groupField
    ? [
        ...groupPathComponents,
        {
          type: "role" as const,
          name: groupField.name,
          displayName: groupField.displayName || groupField.name,
          originalName: groupField.originalName || undefined,
        },
      ]
    : fallbackPrefix.map((path) => ({
        displayName: path,
        name: path,
        type: "path" as const,
      }));

  return {
    layout,
    prefix,
    fields: [],
    components: [
      {
        displayName: layout.displayName || layout.name,
        name: layout.name,
        type: "layout" as const,
      },
      ...presetComponents,
    ],
  };
};

export const buildContactDetailsFixedSelection = ({
  layout,
  contactType,
}: {
  layout: GqlMatterLayout;
  contactType: ContactType;
}) => ({
  layout,
  prefix: [] as string[],
  fields: [],
  components: [
    {
      displayName: layout.displayName || layout.name,
      name: layout.name,
      type: "layout" as const,
    },
    {
      displayName: contactType,
      name: contactType,
      type: "role" as const,
    },
  ],
});

export const getGroupMappedFieldNames = (
  fields: {
    values: { groupUri?: string | null; field?: { name: string } | null };
  }[],
  groupUri: string | undefined,
) =>
  fields
    .map((field) =>
      field.values.groupUri === groupUri ? field.values.field?.name : undefined,
    )
    .filter(isNotNullOrUndefined);

export const buildChangedMappedFields = ({
  selectedFields,
  existingFieldNames,
  groupFields,
}: {
  selectedFields: MatterField[];
  existingFieldNames: string[];
  groupFields: {
    uri: string;
    values: { field?: { name: string } | null };
  }[];
}) => {
  const existingNames = new Set(existingFieldNames);
  const selectedNames = new Set(selectedFields.map((field) => field.name));

  return {
    fieldsToAdd: selectedFields.filter(
      (field) => !existingNames.has(field.name),
    ),
    fieldUrisToRemove: groupFields
      .filter((field) => {
        const name = field.values.field?.name;
        return !!name && existingNames.has(name) && !selectedNames.has(name);
      })
      .map((field) => field.uri),
  };
};

export const hasMappedFieldSelectionChanged = ({
  selectedFieldNames,
  existingFieldNames,
}: {
  selectedFieldNames: string[];
  existingFieldNames: string[];
}) => {
  if (selectedFieldNames.length !== existingFieldNames.length) return true;
  const selectedNames = new Set(selectedFieldNames);
  return existingFieldNames.some((name) => !selectedNames.has(name));
};
